import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { TreeStructure, Plus, ArrowRight, XCircle, MagnifyingGlass } from '@phosphor-icons/react';

const TYPE_COLORS = {
  gene: '#8b5cf6', construct: '#3b82f6', plasmid: '#0d9488', strain: '#f59e0b',
  experiment: '#ef4444', sample: '#ec4899', protocol: '#6366f1',
};

const LineagePage = () => {
  const [graph, setGraph] = useState({ nodes: [], links: [] });
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({
    source_type: 'gene', source_id: '', source_name: '',
    target_type: 'construct', target_id: '', target_name: '',
    relationship: 'derived_from', notes: ''
  });
  const [filter, setFilter] = useState('');

  const load = useCallback(async () => {
    try {
      const g = await api.getLineageGraph();
      setGraph(g);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.source_name || !form.target_name) { toast.error('Names required'); return; }
    try {
      const data = { ...form };
      if (!data.source_id) data.source_id = `${data.source_type}-${Date.now()}`;
      if (!data.target_id) data.target_id = `${data.target_type}-${Date.now()}`;
      await api.createLineageLink(data);
      toast.success('Link created');
      setShowForm(false);
      setForm({ source_type: 'gene', source_id: '', source_name: '', target_type: 'construct', target_id: '', target_name: '', relationship: 'derived_from', notes: '' });
      load();
    } catch (e) { toast.error('Failed'); }
  };

  const filteredNodes = graph.nodes.filter(n => !filter || n.name.toLowerCase().includes(filter.toLowerCase()) || n.type.includes(filter.toLowerCase()));
  const filteredLinks = graph.links.filter(l => {
    if (!filter) return true;
    return l.source_name.toLowerCase().includes(filter.toLowerCase()) || l.target_name.toLowerCase().includes(filter.toLowerCase());
  });

  // Build adjacency for tree view
  const nodesByType = {};
  filteredNodes.forEach(n => { if (!nodesByType[n.type]) nodesByType[n.type] = []; nodesByType[n.type].push(n); });

  if (loading) return <div className="flex items-center justify-center h-64"><div className="spinner" /></div>;

  return (
    <div data-testid="lineage-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="heading-display">Lineage Graph</h1>
          <p className="text-slate-400 mt-1">Track relationships: Gene &rarr; Construct &rarr; Plasmid &rarr; Strain &rarr; Experiment</p>
        </div>
        <Button className="btn-primary flex items-center gap-2" onClick={() => setShowForm(true)} data-testid="add-link-btn">
          <Plus size={16} /> Add Link
        </Button>
      </div>

      {/* Search */}
      <div className="flex items-center gap-3 mb-6">
        <div className="relative flex-1 max-w-sm">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input className="input-field w-full pl-9" placeholder="Filter by name or type..." value={filter} onChange={e => setFilter(e.target.value)} data-testid="lineage-search" />
        </div>
        <div className="flex gap-2 flex-wrap">
          {Object.entries(TYPE_COLORS).map(([type, color]) => (
            <button key={type} className="text-[10px] px-2 py-1 rounded-full font-medium capitalize border border-slate-700 hover:opacity-80 transition" style={{ backgroundColor: `${color}15`, color, borderColor: `${color}40` }} onClick={() => setFilter(type)}>
              {type}
            </button>
          ))}
        </div>
      </div>

      {graph.nodes.length === 0 ? (
        <div className="card-flat p-12 text-center text-slate-400">
          <TreeStructure size={48} className="mx-auto mb-3 opacity-40" />
          <h3 className="text-lg font-medium mb-1" style={{ color: '#F1F5F9' }}>No lineage data yet</h3>
          <p className="text-sm">Start by adding links between genes, constructs, plasmids, strains, and experiments.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Visual Graph Area */}
          <div className="lg:col-span-2 card-flat p-5" data-testid="lineage-visual">
            <h3 className="heading-card mb-4">Connections ({filteredLinks.length})</h3>
            <div className="space-y-2 max-h-[60vh] overflow-y-auto">
              {filteredLinks.map((link, i) => (
                <div key={i} className="flex items-center gap-3 p-3 rounded-lg border border-slate-700 hover:border-slate-700 transition-colors" data-testid={`link-${i}`}>
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: TYPE_COLORS[link.source_type] || '#94a3b8' }} />
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate" style={{ color: '#F1F5F9' }}>{link.source_name}</p>
                      <p className="text-[10px] text-slate-400 capitalize">{link.source_type}</p>
                    </div>
                  </div>
                  <div className="flex flex-col items-center flex-shrink-0 px-2">
                    <ArrowRight size={16} className="text-slate-400" />
                    <span className="text-[9px] text-slate-400 mt-0.5">{link.relationship?.replace('_', ' ')}</span>
                  </div>
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: TYPE_COLORS[link.target_type] || '#94a3b8' }} />
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate" style={{ color: '#F1F5F9' }}>{link.target_name}</p>
                      <p className="text-[10px] text-slate-400 capitalize">{link.target_type}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Entities by Type */}
          <div className="card-flat p-4">
            <h3 className="heading-card mb-3">Entities by Type</h3>
            <div className="space-y-3">
              {Object.entries(nodesByType).map(([type, nodes]) => (
                <div key={type}>
                  <div className="flex items-center gap-2 mb-1.5">
                    <span className="w-3 h-3 rounded" style={{ backgroundColor: TYPE_COLORS[type] || '#94a3b8' }} />
                    <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">{type} ({nodes.length})</span>
                  </div>
                  <div className="pl-5 space-y-1">
                    {nodes.map((n, i) => (
                      <p key={i} className="text-sm text-slate-200 truncate">{n.name}</p>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Add Link Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
          <div className="bg-slate-800 rounded-xl p-6 w-full max-w-lg" onClick={e => e.stopPropagation()} data-testid="link-form">
            <div className="flex items-center justify-between mb-4">
              <h2 className="heading-section">Add Lineage Link</h2>
              <button onClick={() => setShowForm(false)}><XCircle size={20} /></button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="text-xs font-medium text-slate-400 mb-1 block">Source</label>
                <div className="grid grid-cols-3 gap-2">
                  <select className="input-field" value={form.source_type} onChange={e => setForm(p => ({...p, source_type: e.target.value}))}>
                    {Object.keys(TYPE_COLORS).map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                  <input className="input-field col-span-2" placeholder="Source name *" value={form.source_name} onChange={e => setForm(p => ({...p, source_name: e.target.value}))} required data-testid="source-name-input" />
                </div>
              </div>
              <div className="text-center"><ArrowRight size={20} className="mx-auto text-slate-400" /></div>
              <div>
                <label className="text-xs font-medium text-slate-400 mb-1 block">Target</label>
                <div className="grid grid-cols-3 gap-2">
                  <select className="input-field" value={form.target_type} onChange={e => setForm(p => ({...p, target_type: e.target.value}))}>
                    {Object.keys(TYPE_COLORS).map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                  <input className="input-field col-span-2" placeholder="Target name *" value={form.target_name} onChange={e => setForm(p => ({...p, target_name: e.target.value}))} required data-testid="target-name-input" />
                </div>
              </div>
              <select className="input-field w-full" value={form.relationship} onChange={e => setForm(p => ({...p, relationship: e.target.value}))}>
                <option value="derived_from">Derived From</option>
                <option value="contains">Contains</option>
                <option value="used_in">Used In</option>
                <option value="produces">Produces</option>
                <option value="cloned_into">Cloned Into</option>
                <option value="transformed_into">Transformed Into</option>
              </select>
              <textarea className="input-field w-full" rows={2} placeholder="Notes (optional)" value={form.notes} onChange={e => setForm(p => ({...p, notes: e.target.value}))} />
              <Button type="submit" className="btn-primary w-full" data-testid="submit-link-btn">Create Link</Button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default LineagePage;
